import { Card, CardContent, Checkbox, Stack, Typography } from '@mui/material';
import type { AccessCentrePermission } from 'domains/accessCentre/types';

const REQUIRES_PREFIX = 'Requires : ';

/** Display name from a permission key: part after '.' (e.g. "content.view" → "View"). */
const getDisplayNameFromKey = (key: string): string => {
    if (!key) return key;
    const afterDot = key.includes('.') ? key.split('.').slice(1).join('.') : key;
    const withSpaces = afterDot.replace(/_/g, ' ');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
};

const permissionCardStyles = {
    cursor: 'pointer',
    minHeight: 72,
    display: 'flex',
    alignItems: 'flex-start',
};

export type PermissionCardProps = {
    permission: AccessCentrePermission;
    label: string;
    isSelected: boolean;
    onToggle: (id: number) => void;
};

const PermissionCard = ({ permission, label, isSelected, onToggle }: PermissionCardProps) => {
    const hasDependencies = permission.depends_on && permission.depends_on.length > 0;
    const requiresText = hasDependencies
        ? REQUIRES_PREFIX + permission.depends_on!.map(getDisplayNameFromKey).join(', ')
        : REQUIRES_PREFIX + '—';

    return (
        <Card
            variant="outlined"
            sx={{
                ...permissionCardStyles,
                borderColor: isSelected ? 'primary.main' : undefined,
                borderWidth: isSelected ? 2 : 1,
            }}
            onClick={() => onToggle(permission.id)}
            data-testid={`access-center-permission-option-${permission.id}`}
        >
            <CardContent sx={{ py: 1, width: '100%', '&:last-child': { pb: 1 } }}>
                <Stack alignItems="flex-start">
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => onToggle(permission.id)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ 'aria-label': `Select ${label}` }}
                            data-testid={`access-center-permission-checkbox-${permission.id}`}
                        />
                        <Typography variant="body2" noWrap title={label}>
                            {label}
                        </Typography>
                    </Stack>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ pl: '52px', fontSize: '0.75rem' }}
                        data-testid={`access-center-permission-requires-${permission.id}`}
                    >
                        {hasDependencies ? requiresText : ''}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default PermissionCard;
